import { useAnalyticsFavorites } from "../lib/analyticsFavorites";
import AnalyticsHeaderSearch from "./AnalyticsHeaderSearch";
import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, canUseAskAI, isLockedDashboardUser } from "../lib/auth";
import { canSee, isRestricted } from "../lib/screens";
import {
  FileSpreadsheet,
  LayoutDashboard,
  Wand2,
  Crown,
  FileText,
  Database,
  Shield,
  Settings,
  Bell,
  UserCircle,
  LogOut,
  Sparkles,
  Boxes,
  Upload,
  Terminal,
  Target,
  Download,
  ScrollText,
  Building2,
  Table2,
  Workflow,
  Star,
  DollarSign,
  ShoppingCart,
  FlaskConical,
  Factory,
  Warehouse,
  ClipboardList,
  Truck,
  Landmark,
  BarChart3,
  Search,
} from "lucide-react";

const CEO_TAB_ICONS = {
  "ceo.quick-access": Star,
  "ceo.overview": LayoutDashboard,
  "ceo.buyer": DollarSign,
  "ceo.purchase": ShoppingCart,
  "ceo.qc": FlaskConical,
  "ceo.production": Factory,
  "ceo.store": Warehouse,
  "ceo.po": ClipboardList,
  "ceo.shipment": Truck,
  "ceo.commercial": Landmark,
  "ceo.comparisons": BarChart3,
  "ceo.master": Search,
};

const ROW_ONE = [
  { id: "home", label: "Home", to: "/", icon: LayoutDashboard, screens: ["home"] },
  { id: "dashboards", label: "Dashboards", to: "/dashboards?view=saved", icon: LayoutDashboard, screens: ["dashboards"] },
  // { id: "builder", label: "Builder", to: "/builder/new", icon: Wand2, screens: ["builder"] },
  { id: "ms", label: "MS", to: "/ms", icon: FileSpreadsheet, screens: ["ms"] },
  { id: "ceo", label: "CEO Command Center", to: "/ceo-command-center", icon: Crown, screens: ["ceo.quick-access", "ceo.overview", "ceo.buyer", "ceo.purchase", "ceo.qc", "ceo.production", "ceo.store", "ceo.po", "ceo.shipment", "ceo.commercial", "ceo.comparisons", "ceo.master"] },
  { id: "reports", label: "Reports", to: "/reports", icon: FileText, screens: ["reports", "kpi", "exports"] },
  { id: "data", label: "Data", to: "/data-sources", icon: Database, screens: ["database_explorer", "erp_flow_planner", "data_sources", "datasets", "uploads", "sql"] },
  { id: "admin", label: "Admin", to: "/access-management", icon: Shield, screens: ["access_management", "workspaces", "audit"] },
];

const ROW_TWO = {
  ms: [],
  home: [

  ],
  dashboards: [
    { label: "Saved Dashboards", to: "/dashboards?view=saved", screen: "dashboards" },
    { label: "New Dashboard", to: "/builder/new", screen: "builder" },
    { label: "Client Dashboards", to: "/dashboards?view=client", screen: "dashboards" },
    { label: "Assignments", to: "/access-management", screen: "access_management" },
  ],
  builder: [
    { label: "New Dashboard", to: "/builder/new", screen: "builder" },
    { label: "Saved Dashboards", to: "/dashboards?view=saved", screen: "dashboards" },
    { label: "Dashboard List", to: "/dashboards?view=saved", screen: "dashboards" },
    { label: "Data Sources", to: "/data-sources", screen: "data_sources" },
    { label: "Datasets", to: "/datasets", screen: "datasets" },
  ],
  ceo: [
    { label: "Quick Access", to: "/ceo-command-center?tab=quick-access", favoriteCount: true, screen: "ceo.quick-access" },
    { label: "Dashboard", to: "/ceo-command-center", screen: "ceo.overview" },
    { label: "Sales", to: "/ceo-command-center?tab=buyer", screen: "ceo.buyer" },
    { label: "Purchase", to: "/ceo-command-center?tab=purchase", screen: "ceo.purchase" },
    { label: "QA", to: "/ceo-command-center?tab=qc", screen: "ceo.qc" },
    { label: "Process", to: "/ceo-command-center?tab=production", screen: "ceo.production" },
    { label: "Store", to: "/ceo-command-center?tab=store", screen: "ceo.store" },
    { label: "PO", to: "/ceo-command-center?tab=po", screen: "ceo.po" },
    { label: "Dispatch", to: "/ceo-command-center?tab=shipment", screen: "ceo.shipment" },
    { label: "Commercial", to: "/ceo-command-center?tab=commercial", screen: "ceo.commercial" },
    { label: "Comparisons", to: "/ceo-command-center?tab=comparisons", screen: "ceo.comparisons" },
    // Temporarily hide Master Search from CEO navigation.
    // { label: "Master Search", to: "/ceo-command-center?tab=master", screen: "ceo.master" },
    { label: "Reports", to: "/reports", screen: "reports" },
  ],
  reports: [
    { label: "Reports", to: "/reports", icon: FileText, screen: "reports" },
    { label: "KPI Center", to: "/kpi", icon: Target, screen: "kpi" },
    { label: "Exports", to: "/exports", icon: Download, screen: "exports" },
  ],
  data: [
    { label: "Database Explorer", to: "/database-explorer", icon: Table2, screen: "database_explorer" },
    { label: "ERP Flow Planner", to: "/erp-flow-planner", icon: Workflow, screen: "erp_flow_planner" },
    { label: "Data Sources", to: "/data-sources", icon: Database, screen: "data_sources" },
    { label: "Datasets", to: "/datasets", icon: Boxes, screen: "datasets" },
    { label: "Excel Uploads", to: "/uploads", icon: Upload, screen: "uploads" },
    { label: "SQL Editor", to: "/sql", icon: Terminal, screen: "sql" },
  ],
  admin: [
    { label: "Access Management", to: "/access-management", icon: Shield, screen: "access_management" },
    { label: "Workspaces", to: "/workspaces", icon: Building2, screen: "workspaces" },
    { label: "Audit Logs", to: "/audit", icon: ScrollText, screen: "audit" },
    { label: "Exports", to: "/exports", icon: Download, screen: "exports" },
  ],
};

function activeMain(pathname) {
  if (pathname.startsWith("/dashboards")) return "dashboards";
  if (pathname.startsWith("/builder")) return "builder";
  if (pathname === "/ms") return "ms";
  if (pathname.startsWith("/ceo-command-center")) return "ceo";
  if (pathname.startsWith("/reports") || pathname.startsWith("/kpi")) return "reports";
  if (pathname.startsWith("/database-explorer") || pathname.startsWith("/erp-flow-planner") || pathname.startsWith("/datasets") || pathname.startsWith("/data-sources") || pathname.startsWith("/uploads") || pathname.startsWith("/sql")) return "data";
  if (pathname.startsWith("/access-management") || pathname.startsWith("/workspaces") || pathname.startsWith("/audit") || pathname.startsWith("/exports")) return "admin";
  return "home";
}

export default function ERPAnalyticsHeader() {
  const { user, logout } = useAuth();
  const { favorites } = useAnalyticsFavorites();
  const location = useLocation();
  const navigate = useNavigate();
  const main = activeMain(location.pathname);
  const restricted = isRestricted(user);
  const currentTab = new URLSearchParams(location.search).get("tab") || "overview";
  // Role-restricted users get their CEO tabs as top-level modules, without the CEO wrapper.
  const rowTwo = restricted && main === "ceo"
    ? []
    : (ROW_TWO[main] || ROW_TWO.home).filter((item) => canSee(user, item.screen));
  const rowOne = ROW_ONE.filter((item) => canSee(user, item.screens)).flatMap((item) => {
    if (restricted && item.id === "ceo") {
      return ROW_TWO.ceo
        .filter((c) => c.screen.startsWith("ceo.") && canSee(user, c.screen))
        .map((c) => ({
          id: c.screen,
          label: c.label === "Dashboard" ? "CEO Dashboard" : c.label,
          to: c.to,
          icon: CEO_TAB_ICONS[c.screen] || Crown,
          active: main === "ceo" && location.pathname === "/ceo-command-center" && currentTab === c.screen.slice(4),
        }));
    }
    const first = (ROW_TWO[item.id] || []).find((c) => item.screens.includes(c.screen) && canSee(user, c.screen));
    return [{ ...(first ? { ...item, to: first.to } : item), active: main === item.id }];
  });
  const [openMenu, setOpenMenu] = useState("");

  const toggleMenu = (name) => {
    setOpenMenu((current) => (current === name ? "" : name));
  };

  const handleLogout = () => {
    logout();
    navigate("/login", { replace: true });
  };

  const displayRole = user?.access_role_name || String(user?.role || "super_admin").replace(/_/g, " ");

  return (
    <div className="erp-analytics-top">
      <div className="erp-header">
        <div className="erp-company">
          <div className="erp-logo-img">CA</div>
          <div>
            <div className="erp-company-name">Camaroo Analytics</div>
            <div className="erp-powered">Powered By Quantira</div>
          </div>
        </div>

        {user && !isLockedDashboardUser(user) && <AnalyticsHeaderSearch allowAI={canUseAskAI(user)} />}

        <div className="erp-user-area">
          {canUseAskAI(user) && (
            <button
              type="button"
              className="erp-round-btn erp-header-ask-ai"
              onClick={() => window.dispatchEvent(new CustomEvent("quantira:open-ask-ai"))}
            >
              <Sparkles size={16} strokeWidth={1.8} />
              <span>Ask AI</span>
            </button>
          )}
          <div className="erp-menu-anchor">
            <button type="button" className={`erp-round-btn ${openMenu === "settings" ? "active" : ""}`} onClick={() => toggleMenu("settings")}>
              <Settings size={17} strokeWidth={1.8} />
              <span>Settings</span>
            </button>

            {openMenu === "settings" && (
              <div className="erp-header-dropdown settings-panel">
                <div className="erp-dropdown-title">Settings</div>
                {canSee(user, "access_management") && <button type="button" onClick={() => navigate("/access-management")}>Access Management</button>}
                {canSee(user, "workspaces") && <button type="button" onClick={() => navigate("/workspaces")}>Workspaces</button>}
                {canSee(user, "audit") && <button type="button" onClick={() => navigate("/audit")}>Audit Logs</button>}
                {canSee(user, "exports") && <button type="button" onClick={() => navigate("/exports")}>Export History</button>}
              </div>
            )}
          </div>

          <div className="erp-menu-anchor">
            <button type="button" className={`erp-icon-btn ${openMenu === "notifications" ? "active" : ""}`} aria-label="Notifications" onClick={() => toggleMenu("notifications")}>
              <Bell size={17} strokeWidth={1.8} />
            </button>

            {openMenu === "notifications" && (
              <div className="erp-header-dropdown notifications-panel">
                <div className="erp-dropdown-title">Notifications</div>
                <div className="erp-empty-note">No new notifications right now.</div>
                {canSee(user, "audit") && <button type="button" onClick={() => navigate("/audit")}>View recent activity</button>}
              </div>
            )}
          </div>

          <button type="button" className="erp-user-info erp-profile-trigger" onClick={() => toggleMenu("profile")}>
            <strong>{user?.name || "Super Admin"}</strong>
            <span>{displayRole}</span>
          </button>

          <div className="erp-menu-anchor">
            <button type="button" className={`erp-avatar ${openMenu === "profile" ? "active" : ""}`} onClick={() => toggleMenu("profile")} title="Profile">
              <UserCircle size={31} strokeWidth={1.5} />
            </button>

            {openMenu === "profile" && (
              <div className="erp-header-dropdown profile-panel">
                <div className="erp-dropdown-title">Account</div>
                <div className="erp-profile-card">
                  <strong>{user?.name || "Super Admin"}</strong>
                  <span>{user?.email || "superadmin@camaroo.io"}</span>
                  <small>{displayRole}</small>
                </div>
                {canSee(user, "access_management") && <button type="button" onClick={() => navigate("/access-management")}>Manage users</button>}
                <button type="button" className="danger" onClick={handleLogout}>Logout</button>
              </div>
            )}
          </div>

          <button type="button" className="erp-logout" onClick={handleLogout} title="Logout">
            <LogOut size={15} strokeWidth={1.8} />
          </button>
        </div>
      </div>

      <div className="erp-main-menu compact">
        {rowOne.map((item) => {
          const Icon = item.icon;
          return (
            <Link key={item.id} to={item.to} className={`erp-main-btn ${item.active ? "active" : ""}`}>
              <Icon si
              e={18} strokeWidth={1.8} />
              <span>{item.label}</span>
            </Link>
          );
        })}
      </div>

      {rowTwo.length > 0 && <div className="erp-second-menu compact">
        {rowTwo.map((item, index) => {
          const Icon = item.icon;
          const itemUrl = new URL(item.to, window.location.origin);
          const currentTab = new URLSearchParams(location.search).get("tab");
          const itemTab = itemUrl.searchParams.get("tab");

          const itemSearch = itemUrl.search || "";
          const currentSearch = location.search || "";

          const exactPathAndSearch =
            itemUrl.pathname === location.pathname &&
            (itemSearch ? itemSearch === currentSearch : currentSearch === "");

          const isActive =
            main === "home"
              ? (
                  itemUrl.pathname === location.pathname &&
                  ((itemUrl.search || "?section=overview") === (location.search || "?section=overview"))
                )
              : main === "ceo"
                ? (
                      itemUrl.pathname === location.pathname &&
                      ((currentTab || "overview") === (itemTab || "overview"))
                    )
                : (
                    exactPathAndSearch ||
                    (
                      item.to === "/builder/new" &&
                      location.pathname.startsWith("/builder")
                    ) ||
                    (
                      item.to === "/access-management" &&
                      location.pathname.startsWith("/access-management")
                    )
                  );

          return (
            <Link key={`${item.label}-${index}`} to={item.to} className={`erp-pill-btn ${isActive ? "active" : ""}`}>
              {Icon && <Icon size={15} strokeWidth={1.8} />}
              <span>{item.label}{item.favoriteCount ? ` (${favorites.length})` : ""}</span>
            </Link>
          );
        })}
      </div>}
    </div>
  );
}
